import { calculateRemainingCredits } from "@/lib/tier-limits";
/**
 * Display-only cache of the last known profile values (credits, tier, admin
 * flag) so headers can paint correct values on first render instead of
 * flashing placeholders while the network calls resolve.
 *
 * NEVER use this for gating decisions — every credit/permission check must use
 * the freshly loaded profile or the server response.
 */
export interface ProfileSnapshot {
  subscription_tier: "free" | "pro" | "business" | "enterprise";
  bonus_generations: number;
  designs_this_month: number;
  total_generations: number;
  is_admin: boolean;
}

const key = (uid: string) => `dc_profile_${uid}`;

export const readProfileSnapshot = (uid?: string | null): ProfileSnapshot | null => {
  if (!uid) return null;
  try {
    const raw = localStorage.getItem(key(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProfileSnapshot;
    if (typeof parsed?.bonus_generations !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
};

export const writeProfileSnapshot = (uid: string, snapshot: Partial<ProfileSnapshot>) => {
  try {
    const existing = readProfileSnapshot(uid);
    localStorage.setItem(key(uid), JSON.stringify({ ...(existing || {}), ...snapshot }));
  } catch {
    /* storage unavailable — non-fatal */
  }
};

export const clearProfileSnapshot = (uid?: string | null) => {
  try {
    if (uid) localStorage.removeItem(key(uid));
  } catch {
    /* no-op */
  }
};

/**
 * Best-effort snapshot when the user id isn't known yet at first render:
 * returns the most recently written snapshot, if there is exactly one.
 */
export const readLastProfileSnapshot = (): ProfileSnapshot | null => {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith("dc_profile_")) keys.push(k);
    }
    if (keys.length !== 1) return null;
    const parsed = JSON.parse(localStorage.getItem(keys[0]) as string) as ProfileSnapshot;
    return typeof parsed?.bonus_generations === "number" ? parsed : null;
  } catch {
    return null;
  }
};

/**
 * Convenience for mobile headers: last known credit balance + tier, computed
 * from the cached profile. Display-only.
 */
export const readCachedCredits = (): { credits: number; tier: ProfileSnapshot["subscription_tier"] } | null => {
  const snap = readLastProfileSnapshot();
  if (!snap) return null;
  return {
    credits: calculateRemainingCredits(
      snap.subscription_tier,
      snap.designs_this_month,
      snap.bonus_generations
    ),
    tier: snap.subscription_tier,
  };
};
